import React, { Component, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ errorInfo });

    if (import.meta.env.PROD) {
      this.logErrorToBackend(error, errorInfo);
    } else {
      console.error('ErrorBoundary caught:', error, errorInfo);
    }
  }

  logErrorToBackend = async (error: Error, errorInfo: React.ErrorInfo) => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/log-user-error`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${(import.meta.env.VITE_SUPABASE_ANON_KEY)}`,
          },
          body: JSON.stringify({
            error_type: 'react_error',
            error_message: error.message,
            stack_trace: error.stack,
            component_stack: errorInfo.componentStack,
            screen_name: window.location.pathname,
            user_agent: navigator.userAgent,
          }),
        }
      );

      if (!response.ok) {
        console.error('Failed to log error to backend');
      }
    } catch (e) {
      console.error('Error logging to backend:', e);
    }
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>

              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Er ging iets mis
              </h1>

              <p className="text-gray-600 mb-6">
                Er is een onverwachte fout opgetreden. We hebben dit probleem geregistreerd en kijken ernaar.
              </p>

              {import.meta.env.DEV && this.state.error && (
                <div className="w-full mb-6 p-4 bg-gray-50 rounded border border-gray-200 text-left">
                  <p className="text-xs font-mono text-red-600 break-all">
                    {this.state.error.message}
                  </p>
                </div>
              )}

              <div className="flex gap-3 w-full">
                <button
                  onClick={this.handleReset}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#231F20] text-white rounded-lg hover:bg-[#231F20]/90 transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  Probeer opnieuw
                </button>

                <button
                  onClick={this.handleGoHome}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Home className="w-4 h-4" />
                  Dashboard
                </button>
              </div>

              <p className="text-xs text-gray-500 mt-6">
                Blijft dit probleem zich voordoen?{' '}
                <a href="mailto:support@dibbi.nl" className="text-[#231F20] underline">
                  Neem contact op
                </a>
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
